import { ScrollChevronIcon } from '@/utils/helpers';
import { Skeleton } from 'primereact/skeleton';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const SCROLL_STEP_RATIO = 0.8;
const SKELETON_CARDS = [0, 1, 2, 3];
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const RowSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
`;

const RowTitle = styled.h2`
  margin: 0;
  color: var(--black-main);
  font-size: var(--heading-small-font-size);
  font-weight: 600;
  line-height: 1.5;
`;

const ScrollWrapper = styled.div`
  position: relative;
  margin-inline: 0 calc(-1 * var(--default-padding));
`;

const ScrollContainer = styled.div<{ $isFetching?: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  gap: var(--medium-padding);
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  opacity: ${({ $isFetching }) => ($isFetching ? 0.5 : 1)};
  pointer-events: ${({ $isFetching }) => ($isFetching ? 'none' : 'auto')};
  transition: opacity 0.2s ease-in-out;
  transition-delay: 0.2s;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const GradientFade = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  inset-block: 0;
  ${({ $side }) => ($side === 'left' ? 'left: 0;' : 'right: 0;')}
  width: calc(var(--default-padding) + 64px);
  background: linear-gradient(
    ${({ $side }) => ($side === 'left' ? 'to right' : 'to left')},
    var(--blue-darkest-opacity),
    transparent
  );
  pointer-events: none;
  z-index: 1;
`;

const ScrollButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  inset-block: 0;
  ${({ $side }) => ($side === 'left' ? 'left: 0;' : 'right: 0;')}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  padding: 0;
  border: none;
  background: none;
  color: var(--blue-darkest);
  cursor: pointer;
  z-index: 2;

  &:focus-visible {
    outline: 2px solid var(--blue-darkest);
    outline-offset: 2px;
    border-radius: var(--xxxsmall-border-radius);
  }
`;

const CardSkeleton = styled(Skeleton)`
  && {
    flex: none;
  }
`;

const EmptyMessage = styled.p`
  margin: 0;
  padding-block: var(--default-padding-small);
  color: var(--gray-darkest);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
`;

type CourseScrollRowPropsType = {
  title: string;
  emptyMessage?: string;
  children?: React.ReactNode;
  leadCard?: React.ReactNode;
  isLoading?: boolean;
  isFetching?: boolean;
};

export const CourseScrollRow = ({
  title,
  emptyMessage,
  children,
  leadCard,
  isLoading,
  isFetching,
}: CourseScrollRowPropsType) => {
  const { t } = useTranslation();
  const titleId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const cardCount = React.Children.toArray(children).length;
  const hasContent = cardCount > 0 || !!leadCard;

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft + container.clientWidth < container.scrollWidth - 1,
    );
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (container.scrollWidth <= container.clientWidth) return;

      const atStart = container.scrollLeft <= 0;
      const atEnd =
        container.scrollLeft + container.clientWidth >=
        container.scrollWidth - 1;
      if ((event.deltaY < 0 && atStart) || (event.deltaY > 0 && atEnd)) return;

      event.preventDefault();
      container.scrollLeft += event.deltaY;
    };

    updateScrollState();
    container.addEventListener('scroll', updateScrollState, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: false });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      container.removeEventListener('wheel', handleWheel);
      resizeObserver.disconnect();
    };
  }, [updateScrollState, cardCount, hasContent, isLoading]);

  const handleScrollClick = (direction: 'left' | 'right') => {
    const container = scrollRef.current;
    if (!container) return;

    const distance = container.clientWidth * SCROLL_STEP_RATIO;

    container.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    const container = scrollRef.current;
    if (!container) return;

    const items = Array.from(container.children) as HTMLElement[];
    const currentIndex = items.findIndex((item) =>
      item.contains(event.target as Node),
    );
    if (currentIndex === -1) return;

    const nextItem =
      items[currentIndex + (event.key === 'ArrowRight' ? 1 : -1)];
    if (!nextItem) return;

    event.preventDefault();

    const focusable = nextItem.matches(FOCUSABLE_SELECTOR)
      ? nextItem
      : nextItem.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

    if (!focusable) nextItem.tabIndex = -1;

    (focusable ?? nextItem).focus();
    nextItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  };

  const renderRowContent = () => {
    if (isLoading) {
      return (
        <ScrollWrapper>
          <ScrollContainer aria-hidden="true">
            {SKELETON_CARDS.map((skeletonKey) => (
              <CardSkeleton
                key={skeletonKey}
                width="264px"
                height="243px"
                borderRadius="var(--box-medium-border-radius)"
              />
            ))}
          </ScrollContainer>
        </ScrollWrapper>
      );
    }

    if (!hasContent)
      return emptyMessage ? <EmptyMessage>{emptyMessage}</EmptyMessage> : null;

    return (
      <ScrollWrapper>
        {canScrollLeft && (
          <>
            <GradientFade $side="left" />
            <ScrollButton
              type="button"
              $side="left"
              aria-label={t('content.browse.scrollLeft')}
              onClick={() => handleScrollClick('left')}
            >
              <ScrollChevronIcon direction="left" />
            </ScrollButton>
          </>
        )}

        <ScrollContainer
          ref={scrollRef}
          onKeyDown={handleKeyDown}
          $isFetching={isFetching}
        >
          {leadCard}
          {children}
        </ScrollContainer>

        {canScrollRight && (
          <>
            <GradientFade $side="right" />
            <ScrollButton
              type="button"
              $side="right"
              aria-label={t('content.browse.scrollRight')}
              onClick={() => handleScrollClick('right')}
            >
              <ScrollChevronIcon direction="right" />
            </ScrollButton>
          </>
        )}
      </ScrollWrapper>
    );
  };

  return (
    <RowSection aria-labelledby={titleId} aria-busy={isLoading || isFetching}>
      <RowTitle id={titleId}>{title}</RowTitle>
      {renderRowContent()}
    </RowSection>
  );
};
